import { useState } from "react";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  blue: "#185fa5",
  blueLt: "#E6F1FB",
  blueMid: "#B5D4F4",
  teal: "#0f6e56",
  tealLt: "#e1f5ee",
  amber: "#ba7517",
  amberLt: "#FAEEDA",
  violet: "#533ab7",
  violetLt: "#EEEDFE",
  red: "#a32d2d",
  redLt: "#FCEBEB",
  green: "#3b6d11",
  greenLt: "#EAF3DE",
  coral: "#d85a30",
  coralLt: "#FAECE7",
  border: "#e5e7eb",
  text1: "#111827",
  text2: "#374151",
  text3: "#6b7280",
  text4: "#9ca3af",
  bg: "#f9fafb",
  surface: "#ffffff",
  surface2: "#f3f4f6",
};

const font = "'DM Sans', 'Nunito', system-ui, sans-serif";
const mono = "'DM Mono', 'Courier New', monospace";

// ─── Stage definitions ────────────────────────────────────────────────────────
const STAGES = [
  { key: "totalApplied", label: "Applied", color: C.coral, bg: C.coralLt },
  { key: "scheduled", label: "Scheduled", color: C.violet, bg: C.violetLt },
  { key: "processing", label: "Processing", color: C.blue, bg: C.blueLt },
  { key: "hold", label: "On hold", color: C.amber, bg: C.amberLt },
  { key: "selected", label: "Selected", color: C.green, bg: C.greenLt },
];

const ALL_STAGES = [
  ...STAGES,
  { key: "rejected", label: "Rejected", color: C.red, bg: C.redLt },
];

// ─── KPI strip ────────────────────────────────────────────────────────────────
const KpiStrip = ({ rows }) => {
  const totals = STAGES.reduce((acc, s) => {
    acc[s.key] = rows.reduce((sum, r) => sum + (r[s.key] || 0), 0);
    return acc;
  }, {});
  const totalRejected = rows.reduce((sum, r) => sum + (r.rejected || 0), 0);
  const selRate = totals.totalApplied
    ? Math.round((totals.selected / totals.totalApplied) * 100)
    : 0;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      {ALL_STAGES.map((s, i) => {
        const v = s.key === "rejected" ? totalRejected : totals[s.key];
        return (
          <div
            key={s.key}
            style={{
              padding: "8px 10px",
              borderRight: i < 5 ? `1px solid ${C.border}` : "none",
              background: C.surface,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 500,
                color: C.text4,
                letterSpacing: "0.04em",
                marginBottom: 3,
              }}
            >
              {s.label}
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 500,
                color: s.color,
                fontFamily: mono,
                lineHeight: 1,
              }}
            >
              {v}
            </div>
            {s.key === "selected" && (
              <div style={{ fontSize: 9, color: C.text4, marginTop: 2 }}>
                {selRate}% rate
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Funnel bar ───────────────────────────────────────────────────────────────
const FunnelBar = ({ row, maxApplied }) => {
  const BAR_MAX = 140;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      {STAGES.map((s, i) => {
        const v = row[s.key] || 0;
        if (!v) return null;
        const w = Math.max(Math.round((v / maxApplied) * BAR_MAX), 20);
        return (
          <div
            key={s.key}
            title={`${s.label}: ${v}`}
            style={{
              width: w,
              height: 22,
              background: s.color,
              borderRadius: i === 0 ? "4px 3px 3px 4px" : "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 500,
              color: "#fff",
              fontFamily: mono,
              flexShrink: 0,
              cursor: "default",
            }}
          >
            {v}
          </div>
        );
      })}
    </div>
  );
};

// ─── Rate bar ─────────────────────────────────────────────────────────────────
const RateBar = ({ rate }) => {
  const color = rate >= 15 ? C.green : rate >= 8 ? C.amber : C.red;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          flex: 1,
          height: 4,
          background: C.surface2,
          borderRadius: 99,
          overflow: "hidden",
          minWidth: 40,
        }}
      >
        <div
          style={{
            width: `${rate}%`,
            height: "100%",
            background: color,
            borderRadius: 99,
          }}
        />
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 500,
          color: C.text2,
          fontFamily: mono,
          minWidth: 28,
          textAlign: "right",
        }}
      >
        {rate}%
      </span>
    </div>
  );
};

// ─── Legend ───────────────────────────────────────────────────────────────────
const StageLegend = () => (
  <div
    style={{
      display: "flex",
      gap: 10,
      alignItems: "center",
      padding: "6px 14px",
      background: C.bg,
      borderBottom: `1px solid ${C.border}`,
      flexWrap: "wrap",
    }}
  >
    {ALL_STAGES.map((s) => (
      <span
        key={s.key}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 10,
          color: C.text3,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 2,
            background: s.color,
            display: "inline-block",
          }}
        />
        {s.label}
      </span>
    ))}
  </div>
);

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "36px 20px",
      gap: 6,
      color: C.text4,
      fontSize: 12,
    }}
  >
    <span style={{ fontSize: 28 }}>📭</span>
    <span>No job posts match your search</span>
  </div>
);

// ─── Sort icon ────────────────────────────────────────────────────────────────
const SortIcon = ({ k, sortKey, sortDir }) => {
  if (sortKey !== k)
    return (
      <span style={{ color: C.text4, fontSize: 9, marginLeft: 3 }}>⇅</span>
    );
  return (
    <span style={{ color: C.blue, fontSize: 9, marginLeft: 3 }}>
      {sortDir === "desc" ? "↓" : "↑"}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export const HiringPipelineCard = ({ jobPostCandidates = [] }) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("totalApplied");
  const [sortDir, setSortDir] = useState("desc");

  const filtered = jobPostCandidates
    .filter(
      (r) =>
        !search ||
        (r.jobPostName || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.jobPostCode || "").toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === "desc" ? bv - av : av - bv;
    });

  const maxApplied = Math.max(
    ...jobPostCandidates.map((r) => r.totalApplied || 0),
    1,
  );

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const thStyle = (align = "center") => ({
    padding: "7px 10px",
    textAlign: align,
    fontSize: 9,
    fontWeight: 500,
    color: C.text3,
    letterSpacing: "0.05em",
    background: C.bg,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: "nowrap",
    userSelect: "none",
    cursor: "pointer",
  });

  const sortProps = { sortKey, sortDir };

  return (
    <div
      style={{
        background: C.surface,
        borderRadius: 12,
        fontFamily: font,
        overflow: "hidden",
        border: `0.5px solid ${C.border}`,
        fontSize: 12,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: `1px solid ${C.border}`,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              background: C.blueLt,
              border: `1px solid ${C.blueMid}`,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            🚀
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: C.text1,
                lineHeight: 1.2,
              }}
            >
              Hiring pipeline
            </div>
            <div style={{ fontSize: 11, color: C.text4, marginTop: 1 }}>
              Candidate progress across all active job posts
            </div>
          </div>
          {jobPostCandidates.length > 0 && (
            <span
              style={{
                background: C.blueLt,
                color: C.blue,
                fontSize: 10,
                fontWeight: 500,
                padding: "2px 7px",
                borderRadius: 99,
              }}
            >
              {jobPostCandidates.length} posts
            </span>
          )}
        </div>

        {/* Search */}
        <div style={{ position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 13,
              color: C.text4,
              pointerEvents: "none",
            }}
          >
            🔍
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by role or code…"
            style={{
              paddingLeft: 26,
              paddingRight: 10,
              paddingTop: 5,
              paddingBottom: 5,
              fontSize: 11,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              outline: "none",
              fontFamily: font,
              color: C.text1,
              background: C.bg,
              width: 170,
            }}
          />
        </div>
      </div>

      {/* KPI strip */}
      {jobPostCandidates.length > 0 && <KpiStrip rows={jobPostCandidates} />}

      {/* Legend */}
      <StageLegend />

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
          >
            <thead>
              <tr>
                <th style={{ ...thStyle("left"), paddingLeft: 14, width: 200 }}>
                  Role
                </th>
                <th style={thStyle("left")}>Pipeline flow</th>
                {["totalApplied", "selected", "rejected"].map((k) => (
                  <th
                    key={k}
                    style={{ ...thStyle("center"), width: 72 }}
                    onClick={() => handleSort(k)}
                  >
                    {k === "totalApplied"
                      ? "Applied"
                      : k === "selected"
                        ? "Selected"
                        : "Rejected"}
                    <SortIcon k={k} {...sortProps} />
                  </th>
                ))}
                <th
                  style={{ ...thStyle("center"), width: 110 }}
                  onClick={() => handleSort("selected")}
                >
                  Select rate <SortIcon k="selected" {...sortProps} />
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, idx) => {
                const rate = row.totalApplied
                  ? Math.round((row.selected / row.totalApplied) * 100)
                  : 0;
                return (
                  <tr
                    key={row.jobPostId}
                    style={{
                      borderBottom:
                        idx < filtered.length - 1
                          ? `1px solid ${C.border}`
                          : "none",
                    }}
                    onMouseEnter={(e) =>
                      Array.from(e.currentTarget.cells).forEach(
                        (td) => (td.style.background = C.bg),
                      )
                    }
                    onMouseLeave={(e) =>
                      Array.from(e.currentTarget.cells).forEach(
                        (td) => (td.style.background = "transparent"),
                      )
                    }
                  >
                    {/* Role */}
                    <td
                      style={{
                        padding: "8px 10px 8px 14px",
                        verticalAlign: "middle",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            background: C.surface2,
                            border: `1px solid ${C.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 500,
                            color: C.text3,
                            fontFamily: mono,
                            flexShrink: 0,
                          }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: C.text1,
                              lineHeight: 1.3,
                            }}
                          >
                            {row.jobPostName || `Job post #${row.jobPostId}`}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: C.text4,
                              marginTop: 1,
                              fontFamily: mono,
                            }}
                          >
                            {row.jobPostCode}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Funnel bar */}
                    <td
                      style={{ padding: "8px 10px", verticalAlign: "middle" }}
                    >
                      <FunnelBar row={row} maxApplied={maxApplied} />
                    </td>

                    {/* Applied */}
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: C.surface2,
                          color: C.text1,
                          borderRadius: 5,
                          padding: "2px 8px",
                          fontSize: 11,
                          fontWeight: 500,
                          fontFamily: mono,
                          minWidth: 32,
                        }}
                      >
                        {row.totalApplied ?? 0}
                      </span>
                    </td>

                    {/* Selected */}
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: C.greenLt,
                          color: C.green,
                          borderRadius: 5,
                          padding: "2px 8px",
                          fontSize: 11,
                          fontWeight: 500,
                          fontFamily: mono,
                          minWidth: 32,
                        }}
                      >
                        {row.selected ?? 0}
                      </span>
                    </td>

                    {/* Rejected */}
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: C.redLt,
                          color: C.red,
                          borderRadius: 5,
                          padding: "2px 8px",
                          fontSize: 11,
                          fontWeight: 500,
                          fontFamily: mono,
                          minWidth: 32,
                        }}
                      >
                        {row.rejected ?? 0}
                      </span>
                    </td>

                    {/* Rate */}
                    <td
                      style={{
                        padding: "8px 14px",
                        verticalAlign: "middle",
                        minWidth: 100,
                      }}
                    >
                      <RateBar rate={rate} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      {filtered.length > 0 && (
        <div
          style={{
            padding: "8px 14px",
            borderTop: `1px solid ${C.border}`,
            background: C.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 10, color: C.text4 }}>
            Showing {filtered.length} of {jobPostCandidates.length} job posts
          </span>
          <span style={{ fontSize: 10, color: C.text4 }}>
            Rate: <span style={{ color: C.green }}>●</span> ≥15% &nbsp;
            <span style={{ color: C.amber }}>●</span> ≥8% &nbsp;
            <span style={{ color: C.red }}>●</span> &lt;8%
          </span>
        </div>
      )}
    </div>
  );
};

export default HiringPipelineCard;
